/**
 * Level 3 of the hedge hierarchy: one symbol on one account pair.
 *
 * Routed at /pairs/:slug/:symbol. Resolves the symbol → parent pair row
 * via the account-pair detail API, then renders the existing per-pair
 * detail view (decisions, trade history, live drawdown).
 *
 * Kept as a thin wrapper around `PairDetailPage` so we don't duplicate
 * the decision log / DO-state rendering while migrating to the new URL
 * shape.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { DashboardSection } from "@/components/dashboard-section";
import { Skeleton } from "@/components/ui/skeleton";
import { getAccountPair } from "@/lib/api/account-pairs";
import { usePresencePinger } from "@/hooks/use-presence-pinger";
import { PairDetailPage } from "@/pages/pair-detail";

export function SymbolPairDetailPage(): ReactNode {
  const { slug, symbol } = useParams<{ slug: string; symbol: string }>();
  const [resolvedId, setResolvedId] = useState<string | null | undefined>(
    undefined,
  );
  const [loadError, setLoadError] = useState<string | null>(null);

  // Keep the parent account_pair's bots awake while this view is open.
  usePresencePinger(slug ? [slug] : []);

  useEffect(() => {
    if (!slug || !symbol) return;
    const upper = symbol.toUpperCase();
    getAccountPair(slug)
      .then((res) => {
        const match = res.symbolPairs.find(
          (sp) => sp.symbol.toUpperCase() === upper,
        );
        setResolvedId(match?.slug ?? null);
      })
      .catch((e) => {
        setLoadError(e instanceof Error ? e.message : String(e));
        setResolvedId(null);
      });
  }, [slug, symbol]);

  if (resolvedId === undefined) {
    return (
      <div
        aria-busy="true"
        aria-label="Loading symbol hedge"
        className="flex flex-col gap-4 font-sans"
      >
        <DashboardSection>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-3 w-64" />
            <div className="grid grid-cols-2 gap-3 pt-2 sm:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col gap-1.5 rounded-lg border border-border/40 p-3">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-5 w-24" />
                </div>
              ))}
            </div>
          </div>
        </DashboardSection>
        <DashboardSection>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-4 w-40" />
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-8 w-full rounded-md" />
            ))}
          </div>
        </DashboardSection>
      </div>
    );
  }
  if (resolvedId === null) {
    return (
      <DashboardSection>
        <div className="flex flex-col items-center justify-center py-16 text-center font-sans">
          <p aria-live="polite" className="text-sm text-destructive">
            {loadError ??
              `No active hedge for ${symbol?.toUpperCase()} on this account pair.`}
          </p>
        </div>
      </DashboardSection>
    );
  }

  return <PairDetailPage idOverride={resolvedId} />;
}
